/**
 * **Webhooks salientes** — catálogo tipado de TODO lo que el sistema puede avisar hacia
 * afuera (hoy, canales de Slack por Incoming Webhook).
 *
 * Pedido de Damián (22-oct-2026): «en configuración deberás ponerme todos los webhooks del
 * sistema así mañana vinculo Slack y vamos notificando cosas». Cada entrada es una fila de
 * Ajustes → Integraciones → Webhooks: su URL (cifrada en la base, `outgoingWebhooks`), su
 * switch y el resultado de la última entrega.
 *
 * Es la fuente única: el backend valida contra `WEBHOOK_KEYS`, `emitWebhook(key, …)` sólo
 * acepta una clave de acá y la pantalla dibuja nombre, descripción y eventos desde acá.
 * Agregar un aviso saliente es agregar una entrada, no tocar la pantalla.
 */
import { z } from "zod";
export const WEBHOOK_KEYS = [
    "caja",
    "cierres",
    "seguridad",
    "crm",
    "general",
    "kiosko",
    "negocio",
    "molinete",
    "facturacion",
    "debito",
    "posnet",
    "acceso",
    "profesores",
    "sueldos",
    "clases",
    "planes",
    "backup",
];
const ERROR = "#D5382E";
const ACCENT = "#2E9C94";
const WARNING = "#C47F0A";
const INDIGO = "#4B66C9";
export const WEBHOOK_CATALOG = {
    caja: {
        key: "caja",
        title: "Caja — diferencias",
        description: "Sobrantes y faltantes al cerrar un turno de caja que superan el umbral de la alerta de diferencia de caja.",
        events: ["Diferencia de caja detectada al cerrar un turno"],
        color: ERROR,
    },
    cierres: {
        key: "cierres",
        title: "Caja — cierres y sobres",
        description: "Cada cierre de turno de caja (con o sin diferencia) y los sobres que quedaron en la sede más horas de las permitidas. Llevan importes: mandalo a un canal de Dueño y Administración.",
        events: ["Cierre de turno de caja", "Sobre sin retirar de la sede"],
        color: ACCENT,
    },
    seguridad: {
        key: "seguridad",
        title: "Seguridad — excepciones",
        description: "Las mismas alertas de seguridad que llegan por mail y push, en el momento del hecho. Conviene un canal que sólo vea el Dueño.",
        events: [
            "Cobro anulado",
            "Venta anulada",
            "Descuento sobre el umbral",
            "Diferencia de caja sobre el umbral",
            "Ingreso autorizado con excepción",
            "Cambio de la lista de precios",
            "Cambio de rol, alta o baja de usuario",
            "Exportación masiva de datos",
            "Congelamiento especial",
            "Usuario bloqueado por intentos fallidos",
            "Cambio o revocación de credenciales de una integración",
        ],
        color: ERROR,
    },
    crm: {
        key: "crm",
        title: "CRM — interesados",
        description: "Nuevos interesados y clases de prueba, para que el equipo comercial los vea al toque.",
        events: ["Nuevo interesado", "Clase de prueba agendada"],
        pendingNote: "Todavía no sale ningún aviso por acá: queda listo para cuando el CRM empiece a mandarlos.",
        color: ACCENT,
    },
    general: {
        key: "general",
        title: "General",
        description: "Avisos generales del club que no son de caja, seguridad ni comerciales.",
        events: ["Avisos generales del club"],
        pendingNote: "Todavía no sale ningún aviso automático por acá: sirve para probar el canal y para los avisos que se sumen.",
        color: ACCENT,
    },
    kiosko: {
        key: "kiosko",
        title: "Kiosko",
        description: "Lo que pasa en el kiosko de cada sede: ventas, faltante de mercadería, retiros del equipo y precios a revisar.",
        events: [
            "Venta del kiosko",
            "Producto con bajo stock",
            "Consumo interno (retiro de dueños o staff)",
            "Pedido de la tablet cobrado sin stock",
            "Precio por debajo del costo",
            "Margen por debajo del objetivo",
        ],
        color: WARNING,
    },
    negocio: {
        key: "negocio",
        title: "Negocio — caídas y resumen del día",
        description: "Caída de asistencia o de facturación contra el promedio del mismo día de la semana, y el resumen del día de las 21. Incluye facturación: mandalo a un canal que sólo vea el Dueño.",
        events: [
            "Caída de asistencia",
            "Caída de facturación",
            "Resumen del día (facturación, asistentes y objetivo)",
        ],
        color: INDIGO,
    },
    molinete: {
        key: "molinete",
        title: "Molinetes",
        description: "El molinete dejó pasar a alguien que el sistema no habilitaría: el padrón del equipo está desincronizado.",
        events: ["Molinete desincronizado"],
        color: WARNING,
    },
    facturacion: {
        key: "facturacion",
        title: "Facturación",
        description: "Comprobantes emitidos con CAE que no se le pudieron mandar al socio por mail.",
        events: ["Comprobante sin casilla de envío"],
        color: WARNING,
    },
    debito: {
        key: "debito",
        title: "Débito automático",
        description: "La corrida diaria del débito automático con tarjeta: su resumen y cualquier problema que le impidió cobrar.",
        events: [
            "Resumen de los débitos del día",
            "La corrida de débitos falló",
            "Payway no aceptó el cobro (configuración)",
            "El débito automático está apagado",
            "Cuotas que quedaron sin presentar al débito",
        ],
        color: INDIGO,
    },
    posnet: {
        key: "posnet",
        title: "Posnet",
        description: "Problemas con las terminales de cobro de cada sede y el resumen de los cupones de posnet cargados a mano.",
        events: [
            "Cobro por posnet sin resolver",
            "La terminal no cerró el lote",
            "La terminal no respondió al cierre de lote",
            "Cupones de posnet cargados a mano (resumen del día)",
        ],
        color: WARNING,
    },
    acceso: {
        key: "acceso",
        title: "Acceso — ingresos a revisar",
        description: "Ingresos que necesitan que alguien los mire: los cargados en contingencia que se habrían rechazado y las excepciones repetidas a una misma persona.",
        events: ["Ingresos de contingencia a revisar", "Excepciones de ingreso repetidas"],
        color: WARNING,
    },
    profesores: {
        key: "profesores",
        title: "Profesores",
        description: "Novedades del equipo docente que piden una acción de la coordinación: ausencias avisadas y profes que quedan fuera de personal training.",
        events: ["Un profe avisó que falta a una clase", "Un profe quedó fuera de personal training"],
        color: ACCENT,
    },
    sueldos: {
        key: "sueldos",
        title: "Profesores — liquidaciones y sobres",
        description: "La pre-liquidación del mes lista para revisar y cada sobre de profe entregado en recepción. Llevan importes: mandalo a un canal de Dueño y Administración.",
        events: ["Pre-liquidación del mes lista para revisar", "Sobre de profe entregado"],
        color: INDIGO,
    },
    clases: {
        key: "clases",
        title: "Clases sin inscriptos",
        description: "Clases que a una hora del inicio no tienen a nadie anotado, para que la coordinación lo sepa. Es sólo un aviso: la clase se dicta igual.",
        events: ["Clase sin inscriptos a una hora del inicio"],
        color: WARNING,
    },
    planes: {
        key: "planes",
        title: "Planes y precios",
        description: "Planes cuya oferta vence pronto o ya venció y planes activos que la lista de precios vigente no tiene con precio, para actualizarlos a tiempo.",
        events: [
            "Planes por vencer",
            "Planes vencidos",
            "Planes activos sin precio en la lista vigente",
        ],
        color: WARNING,
    },
    backup: {
        key: "backup",
        title: "Backup",
        description: "La prueba automática de restore del backup, cuando falla o no se pudo hacer. Una prueba que sale bien no avisa por acá.",
        events: ["El restore del backup falló", "El backup no se pudo probar"],
        color: ERROR,
    },
};
export function isWebhookKey(value) {
    return WEBHOOK_KEYS.includes(value);
}
export function getWebhookDefinition(key) {
    return WEBHOOK_CATALOG[key];
}
/**
 * Las categorías del viejo `alerts.slack.webhooks` y a qué webhook pasan. `default` era la
 * URL de respaldo de cualquier canal sin la suya: la migración se la da a cada categoría
 * que no tenía URL propia (`backend/src/lib/webhooks/config.ts`).
 */
export const LEGACY_SLACK_CHANNEL_TO_WEBHOOK = {
    caja: "caja",
    security: "seguridad",
    crm: "crm",
    general: "general",
    default: "general",
};
/**
 * Máscara de una URL de webhook: host + los últimos 4 caracteres
 * (`https://hooks.slack.com/…/AbCd`). Lo que identifica de qué canal es sin dar lo que
 * hace falta para postear en él: la URL entera ES la credencial.
 */
export function maskWebhookUrl(url) {
    if (!url)
        return null;
    let host = "";
    try {
        host = new URL(url).host;
    }
    catch {
        return "cargada";
    }
    const tail = url.replace(/\/+$/, "").slice(-4);
    return `https://${host}/…/${tail}`;
}
/** URL de webhook válida: `https://` con host. Un `http://` viajaría el secreto en claro. */
export const webhookUrlSchema = z
    .string()
    .trim()
    .max(2_000)
    .refine((value) => {
    try {
        const url = new URL(value);
        return url.protocol === "https:" && url.host.length > 0;
    }
    catch {
        return false;
    }
}, "La URL del webhook tiene que empezar con https://");
export const webhookKeyParam = z.object({ key: z.enum(WEBHOOK_KEYS) });
/**
 * `PUT /api/webhooks/:key`. `url` ausente = «no la toqué» (la pantalla nunca recibió la
 * URL real y no puede devolverla); `url: null` = sacarla, y el webhook queda apagado.
 */
export const updateWebhookBody = z
    .object({
    url: webhookUrlSchema.nullable().optional(),
    enabled: z.boolean().optional(),
})
    .refine((body) => body.url !== undefined || body.enabled !== undefined, {
    message: "Mandá la URL, el switch o los dos.",
});
//# sourceMappingURL=webhooks.js.map